import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { fetchWithCache } from '../lib/fetchWithCache';
import { SpeciesCompositionRow, SpeciesStats } from '../lib/types';

interface UseSpeciesStatsResult {
  data: SpeciesStats | null;
  isLoading: boolean;
  error: string | null;
}

// CSV schema: rank,species_code,species_name,fraction_of_biomass,biomass_mg
// Rows are ordered by rank ascending (= fraction descending). The final row is
// the "Other" rollup (species_code === "0000", rank = N+1) covering species
// that were below the 0.5% biomass threshold in the precompute step.
const OTHER_SPECIES_CODE = '0000';

const parseSpeciesStats = (text: string): SpeciesStats => {
  const rows = parseCsv(text);
  const parsed: SpeciesCompositionRow[] = [];
  rows.forEach(r => {
    const fraction = Number(r['fraction_of_biomass']);
    if (!Number.isFinite(fraction)) return;
    const code = r['species_code'];
    parsed.push({
      rank: Number(r['rank']),
      speciesCode: code,
      speciesName: r['species_name'],
      fractionOfBiomass: fraction,
      biomassMg: Number(r['biomass_mg']),
      isOther: code === OTHER_SPECIES_CODE,
    });
  });

  // A valid shed CSV always carries at least the Other rollup row, so an
  // empty `parsed` means the file is malformed (missing column, all fractions
  // non-numeric, etc.). Throwing here routes the failure through the hook's
  // `.catch` so the UI renders the error banner instead of a silent blank.
  if (parsed.length === 0) {
    throw new Error('Species CSV contained no valid composition rows');
  }

  // Shannon includes Other because the Other bucket represents many
  // low-abundance species collapsed into one proportion; dropping it would
  // understate the long tail. Simpson uses 1 - Σ p² (Gini-Simpson) so higher
  // values = more diverse — same direction as Shannon.
  let shannon = 0;
  let sumP2 = 0;
  parsed.forEach(r => {
    const p = r.fractionOfBiomass;
    if (p > 0) shannon += -p * Math.log(p);
    sumP2 += p * p;
  });
  const simpson = 1 - sumP2;

  // Richness is the count of named species in the CSV (excluding the Other
  // rollup). Pielou's evenness J = H / ln(S) is undefined for S < 2.
  const richness = parsed.filter(r => !r.isOther).length;
  const evenness = richness >= 2 ? shannon / Math.log(richness) : 0;

  return { rows: parsed, shannon, simpson, evenness, richness };
};

/**
 * Fetches species composition + derived diversity indices for the supply shed
 * defined by (millCode, driveTime). CSV lives at
 * sfi/species/{mill}_{driveTime}min_species.csv.
 */
export const useSpeciesStats = (
  millCode: string | null,
  driveTimeMinutes: number,
  enabled: boolean = true,
): UseSpeciesStatsResult => {
  const [data, setData] = useState<SpeciesStats | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.speciesStats(millCode, driveTimeMinutes)}`;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // Clear stale data from the previous (mill, driveTime) so a failed refetch
    // doesn't leave the old chart rendered alongside the error banner.
    setData(null);

    fetchWithCache(url, r => r.text().then(parseSpeciesStats))
      .then(parsed => {
        if (cancelled) return;
        setData(parsed);
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(`Failed to load species composition data: ${err.message}`);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTimeMinutes, enabled]);

  return { data, isLoading, error };
};
